const _ = require("lodash");
const WordWrap = require("./util/WordWrap");
const Helpers = require("./util/RenderHelpers");
const {FLAGS} = require("../util/GlobalFlags");

// Various functions for laying out nodes horizontally as you would in a line
// of text. Also includes word wrapping.

const drawInline = function(rc, x, y, children, settings = {}) {
	const {onPreDraw = null, onPostDraw = null, blocksInLine = false} = settings;
	let currX = x;
	let count = 0;
	for (let i = 0; i < children.length; i += 1) {
		const child = children[i];
		const childMeasure = rc.getMeasure(child);
		const childWidth = Helpers.computeNodeFullWidth(childMeasure);
		if (onPreDraw) {
			count = onPreDraw(count, child);
		}
		const childTop = childMeasure.top
			? childMeasure.top
			: childMeasure.height / 2;
		rc.drawNode(
			rc,
			currX + (childMeasure.xPadding || 0),
			y - (rc.isBlockNode(child) && blocksInLine ? childTop : 0),
			child
		);
		currX += childWidth;
		if (onPostDraw) {
			onPostDraw(count, child);
		}
	}
};

const drawBlock = function(rc, x, y, blockChild, measure) {
	const {width, top, yPadding, scaleFactor, centered} = measure;
	let baseX = x + (rc.topNodeWidth - width) / 2;
	if (centered) {
		baseX = x;
	}
	const baseY = y - top + yPadding;
	rc.context.save();
	if (scaleFactor !== 1) {
		// If we're scaling, then we're making sure rawWidth and width match
		// when drawing. x should not be offset at all.
		baseX = x;
		rc.context.translate(baseX, baseY);
		rc.context.scale(scaleFactor, scaleFactor);
		rc.context.translate(-baseX, -baseY);
	}
	rc.drawNode(rc, baseX, baseY, blockChild);
	rc.context.restore();
};

const measureInline = function(rc, fontInfo, props, children) {
	// We want the final measurement of this node to not include any trailing
	// whitespace. The first var tracks all width seen so far, and the second
	// var only tracks up to the last nonwhitespace node.
	let currWidthWithWs = 0;
	let currWidth = 0;
	let maxTop = 0;
	let maxBottom = 0;
	for (let i = 0; i < children.length; i += 1) {
		const childMeasure = rc.measureNode(rc, fontInfo, props, children[i]);
		const yPadding = childMeasure.yPadding || 0;
		currWidthWithWs += Helpers.computeNodeFullWidth(childMeasure);
		// We only update currWidth when we have nonwhitespace.
		if (!rc.isSpaceNode(children[i], {notManual: true})) {
			currWidth = currWidthWithWs;
		}
		const childTop = childMeasure.hasOwnProperty("top")
			? childMeasure.top
			: childMeasure.height / 2;
		const childBottom = childMeasure.hasOwnProperty("bottom")
			? childMeasure.bottom
			: childMeasure.height / 2;
		maxTop = Math.max(maxTop, childTop + yPadding);
		maxBottom = Math.max(maxBottom, childBottom + yPadding);
	}
	// Throw currWidthWithWs away.
	return {
		width: currWidth,
		top: maxTop,
		bottom: maxBottom,
		fontInfo,
	};
};

// Returns an array of lines, each of which are an array of nodes.
const wrap = function(rc, wrapInfo, node) {
	const {maxLineWidth, willBeFit} = wrapInfo;

	// Format the node's children to pass to the WordWrap module.
	const wordList = node.children.map((child, i) => {
		const totalWidth = Helpers.computeNodeFullWidth(
			rc.getMeasure(node.children[i])
		);

		const isSpace = rc.isSpaceNode(child) && child.type !== "lineBreak";
		return {
			width: totalWidth,
			isSpace,
			isManualSpace: isSpace && child.manual,
			isLineBreak: child.type === "lineBreak",
			isBlock: rc.isBlockNode(child),
		};
	});

	// Delegate to WordWrap.
	const result = WordWrap.doWordWrap(maxLineWidth, wordList);

	// Process the errors.
	if (!willBeFit) {
		result.errors.forEach(function(indList) {
			const children = indList.map(i => node.children[i]);
			if (!FLAGS.suppressCMLineOverflow) {
				// Push error message.
				const typeList = children.map(n => `'${n.type}'`).join(", ");
				const width = WordWrap.getWordListWidth(indList.map(i => wordList[i]));
				rc.pushError(
					"Node block with types " +
						typeList +
						" " +
						"has width " +
						width +
						" and overflows a line on its own."
				);
			}
			// Flag each node.
			children.forEach(n => rc.setErrored(n));
		});
	}

	return result.lines.map(indList => indList.map(i => node.children[i]));
};

const drawWrapping = function(rc, x, y, node, measure) {
	let currY = y;
	const maxWidth = measure.width;
	measure.lines.forEach((line, i) => {
		const lineMeasure = measure.lineMeasures[i];
		currY += lineMeasure.top;
		let lineX = x;
		if (measure.centered) {
			lineX += maxWidth / 2 - lineMeasure.width / 2;
		}
		rc.pushLineY(currY, lineMeasure.top, lineMeasure.bottom, measure.fontInfo);
		if (lineMeasure.isBlock) {
			drawBlock(rc, lineX, currY, line[0], lineMeasure);
		} else {
			drawInline(rc, lineX, currY, line);
		}
		rc.popLineY();
		currY += lineMeasure.bottom + (measure.yPadding || 0);
	});
};

// Helper for measureWrapping.
const measureWrappedLine = function(rc, fontInfo, props, nodeList) {
	const {
		skipLinePadding,
		skipBlockPadding,
		minScale,
		maxLineWidth,
		centered,
	} = props;
	const isBlock = nodeList[0] && rc.isBlockNode(nodeList[0]);
	const linePadding = skipLinePadding ? 0 : Helpers.getLinePadding(fontInfo);

	const measure = measureInline(rc, fontInfo, props, nodeList);
	measure.top = Math.max(measure.top, fontInfo.size / 2);
	measure.bottom = Math.max(measure.bottom, fontInfo.size / 2);

	if (!isBlock) {
		return _.extend({}, measure, {
			top: measure.top + linePadding,
			bottom: measure.bottom + linePadding,
			maxLineWidth,
			centered,
			isBlock: false,
		});
	}

	const height = measure.top + measure.bottom;
	const yPadding = skipBlockPadding
		? 0
		: Helpers.getLineWidth(rc, fontInfo) * 5;
	let scaleFactor = 1;
	if (maxLineWidth && measure.width > maxLineWidth) {
		scaleFactor = maxLineWidth / measure.width;
		if (!FLAGS.suppressCMLineOverflow && minScale && scaleFactor < minScale) {
			rc.pushError("Line is too wide; scaling it to fit makes it too small.");
		}
	}
	// measureTop and measureBottom are the heights of the areas above/below the numberline
	// TODO: should top and bottom be these values? it's difficult to determine whether anything would break
	return {
		width: scaleFactor * measure.width,
		top: (scaleFactor * height) / 2 + yPadding,
		bottom: (scaleFactor * height) / 2 + yPadding,
		measureTop: scaleFactor * measure.top + yPadding,
		measureBottom: scaleFactor * measure.bottom + yPadding,
		fontInfo,
		maxLineWidth,
		centered,
		yPadding,
		scaleFactor,
		isBlock: true,
	};
};

const measureWrapping = function(rc, fontInfo, props, node, options = {}) {
	const {
		// Amount of padding to apply to each line past the first.
		yPadding = 0,
		// If true, do not account for child's centered property.
		preventCenter = false,
	} = options;

	// Put measures on the children.
	measureInline(rc, fontInfo, props, node.children);

	const lines = wrap(rc, props, node);
	const lineMeasures = lines.map(nodeList =>
		measureWrappedLine(rc, fontInfo, props, nodeList)
	);

	let maxWidth = 0;
	let currHeight = 0;
	lineMeasures.forEach(lineMeasure => {
		maxWidth = Math.max(maxWidth, lineMeasure.width);
		currHeight += lineMeasure.top + lineMeasure.bottom + yPadding;
	});
	currHeight -= yPadding;

	const centered = props.centered && props.maxLineWidth && !preventCenter;
	if (props.forceFullWidth || centered) {
		maxWidth = Math.max(props.maxLineWidth, maxWidth) || maxWidth;
	}

	return {
		width: maxWidth,
		height: currHeight,
		fontInfo,
		centered,
		yPadding,
		lines,
		lineMeasures,
	};
};

module.exports = {
	drawInline,
	drawBlock,
	measureInline,
	wrap,
	drawWrapping,
	measureWrapping,
};
